// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { delay } from "@eisinc/App/Utilities/helpers";
import { addToChainBefore, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    LoadProducts,
    LoadTopSellers,
} from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

/**
 * @param props
 * @override add additoinal expands
 */
export const XngAddCustomExpand: any = (props: any) => {
    const additionalExpands = "getbadges,getstyledchildrencount,properties";
    props.getProductCollectionParameter = {
        ...props.getProductCollectionParameter,
        additionalExpands,
    };
};

/**
 * @param props
 * @override
 */
export const XngLoadFeaturedProducts: any = (props: any) => {
    const {
        parameter: { carouselType, category, brand },
    } = props;

    if (carouselType !== "featuredProducts" || (!category && !brand)) {
        return;
    }

    props.getProductCollectionParameter = {
        category,
        brand,
        relationship: "featuredProducts",
    };
};

/**
 * @param props
 * @override make featured categories force load products
 */

export const xLoadProducts: typeof LoadProducts = async ({
    dispatch,
    getProductCollectionParameter,
    extraProductCollectionParameter,
    additionalSort,
    parameter: { carouselId, carouselType, numberOfProductsToDisplay, isProductDetailsPage, productId, isShellEdit },
    // XNG-Change
    getState,
}) => {
    if (!getProductCollectionParameter) {
        return;
    }

    const extraProductOptions = extraProductCollectionParameter
        ? {
            getProductCollectionParameter: extraProductCollectionParameter,
            numberOfProductsToDisplay,
        }
        : undefined;

    const isRecentlyViewedOnPDP = carouselType === "recentlyViewed" && isProductDetailsPage;

    getProductCollectionParameter.expand
        ? getProductCollectionParameter.expand.push("images", "badges")
        : (getProductCollectionParameter.expand = ["images", "badges"]);

    // <XNG-Change>: prevent double requests in case we have recently viewed carousel type
    const isFeaturedProducts = carouselType === "featuredProducts";
    let forceLoad = isRecentlyViewedOnPDP || isFeaturedProducts;

    if (isProductDetailsPage) {
        forceLoad = true;
        await new Promise(resolve => { setTimeout(() => resolve(true), 100) });
    }
    // </XNG-Change>

    // <XNG-Change> refetch recently viewed
    const lastTimeRecentlyViewed = (getState() as CustomAppState).xCommon.lastTimeRecentlyViewed;
    const time = Date.now();
    if (carouselType === "recentlyViewed") {
        const isExceed = lastTimeRecentlyViewed ? time - lastTimeRecentlyViewed >= 30000 : true;
        if (isExceed) {
            dispatch({
                type: "xCommon/update",
                data: {
                    key: "lastTimeRecentlyViewed",
                    value: time,
                },
            } as any);
            // Clean up previous one if any
            if (lastTimeRecentlyViewed) {
                dispatch({
                    type: "Data/Products/CompleteLoadProducts",
                    collection: { products: [] } as any,
                    parameter: { ...getProductCollectionParameter, time: lastTimeRecentlyViewed },
                });
            }
            (getProductCollectionParameter as any).time = time;
            await delay(100);
        } else {
            (getProductCollectionParameter as any).time = lastTimeRecentlyViewed;
        }
    }
    // </XNG-Change>
    dispatch(
        loadProductInfoList({
            id: carouselId,
            getProductCollectionParameter,
            extraProductOptions,
            excludeProductId: isRecentlyViewedOnPDP ? productId : undefined,
            // XNG-Changes
            forceLoad,
            isShellEdit,
            additionalSort,
            searchHub: carouselType === "coveoRecommendedProducts" ? "recs" : "",
        } as any),
    );
};

export const xLoadTopSellers: any = (props: any) => {
    const {
        parameter: {
            carouselType,
            numberOfProductsToDisplay,
            displayProductsFrom,
            selectedCategoryIds,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
            brandIdsSelected,
        },
        getState,
    } = props;

    if (carouselType !== "topSellers") {
        return;
    }

    const state = getState();
    if (!getSettingsCollection(state).websiteSettings.enableDynamicRecommendations) {
        return;
    }

    let brandIds;

    if (brandIdsSelected && brandIdsSelected.length) {
        brandIds = brandIdsSelected;
    } else {
        brandIds = isBrandDetailsPage && brand ? [brand.id] : [];
    }

    let topSellersCategoryIds: string[] | undefined;
    let topSellersPersonaIds: string[] | undefined;
    if (displayProductsFrom === "allCategories") {
        topSellersCategoryIds = isProductListPage && category ? [category.id] : undefined;
    } else if (displayProductsFrom === "selectedCategories") {
        topSellersCategoryIds = selectedCategoryIds || [];
    } else if (displayProductsFrom === "customerSegments") {
        topSellersPersonaIds = state.context.session.personas?.map((o: any) => o.id);
    }

    if (isProductListPage && (!topSellersCategoryIds || topSellersCategoryIds.length === 0) && brandIds.length === 0) {
        return;
    }

    props.getProductCollectionParameter = {
        topSellersCategoryIds,
        topSellersPersonaIds,
        pageSize: numberOfProductsToDisplay,
        brandIds,
        makeBrandUrls: brandIds.length > 0,
        filter: "topSellers",
    };
};

export const xLoadCoveoRecommendedProducts: any = (props: any) => {
    const {
        parameter: { carouselType },
    } = props;
    if (carouselType !== "coveoRecommendedProducts") {
        return;
    }

    props.getProductCollectionParameter = {
        query: "",
        actionsHistory: {
            name: "PageView",
            time: new Date(),
            searchHub: "listing",
            documentLocation: document?.location.href,
            documentReferrer: document?.referrer,
        },
        numberOfResults: 50,
        relationship: "coveoRecommendedProducts",
    } as any;
};

addToChainBefore(chain, LoadProducts, XngAddCustomExpand);
addToChainBefore(chain, XngAddCustomExpand, XngLoadFeaturedProducts);
addToChainBefore(chain, XngLoadFeaturedProducts, xLoadCoveoRecommendedProducts);
replaceInChain(chain, LoadProducts, xLoadProducts);
replaceInChain(chain, LoadTopSellers, xLoadTopSellers);
